// Where in a rendered DOCX an agent `highlight_text` request lands (MAR-382).
// Pure DOM walking and string mapping, so it runs under jsdom without the viewer. `page` plays no
// part: docx-preview's page breaks are layout, not an addressable page model.
//
// The match runs on ONE normalized string — lowercased, every whitespace run collapsed to a single
// space — built together with an index map from each normalized character back to the (node, offset)
// it came from. The previous version collapsed whitespace in the concatenated text and then read the
// match position as if it were a raw offset, and walked docx-preview's injected `<style>` block too;
// every collapsed run before the target pulled the highlight earlier ("Concepto pago" painted the
// "Monto del recibo" line two rows up).

export interface DocxHighlightSegment {
  node: Text
  /** Offsets into `node.data`, end exclusive — ready for `Range.setStart`/`setEnd`. */
  start: number
  end: number
}

// Text inside these is never rendered document text (docx-preview puts its `<style>` blocks straight
// into the render target when no separate style container is passed). It also hides a vertically
// merged table cell with an inline `display: none`, checked below.
const NON_RENDERED = new Set(['STYLE', 'SCRIPT', 'NOSCRIPT', 'TEMPLATE'])
// Crossing into a different one of these is a visual line break, so it counts as whitespace.
// Anything finer (docx-preview's per-run `<span>`s) is NOT a boundary: a word split across two runs
// must still match as one word.
const BLOCK = 'p, li, td, th, tr, h1, h2, h3, h4, h5, h6, div, section, article, header, footer'

export function collectHighlightTextNodes(root: Node): Text[] {
  const doc = root.ownerDocument ?? (root as Document)
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      for (let el = node.parentElement; el && el !== root; el = el.parentElement) {
        if (NON_RENDERED.has(el.tagName) || (el as HTMLElement).style?.display === 'none') return NodeFilter.FILTER_REJECT
      }
      return NodeFilter.FILTER_ACCEPT
    },
  })
  const nodes: Text[] = []
  let node: Text | null
  while ((node = walker.nextNode() as Text | null)) nodes.push(node)
  return nodes
}

interface Position { node: number; offset: number }

/** The normalized text, and for each of its characters the raw position it came from (null for a
 *  space standing in for a block boundary, which has no character of its own). */
export function buildNormalizedDocxText(nodes: Text[]): { text: string; map: Array<Position | null> } {
  let text = ''
  const map: Array<Position | null> = []
  let lastBlock: Element | null = null
  const pushSpace = (pos: Position | null) => {
    if (text.length > 0 && !text.endsWith(' ')) { text += ' '; map.push(pos) }
  }
  nodes.forEach((node, n) => {
    const block = node.parentElement?.closest(BLOCK) ?? null
    if (lastBlock && block !== lastBlock) pushSpace(null)
    lastBlock = block
    const data = node.data
    for (let i = 0; i < data.length; i++) {
      if (/\s/.test(data[i]!)) { pushSpace({ node: n, offset: i }); continue }
      for (const ch of data[i]!.toLowerCase()) { text += ch; map.push({ node: n, offset: i }) }
    }
  })
  return { text, map }
}

export function normalizeDocxTarget(target: string): string {
  return target.toLowerCase().replace(/\s+/g, ' ').trim()
}

export function findDocxHighlightSegments(root: Node, target: string): DocxHighlightSegment[] | null {
  const needle = normalizeDocxTarget(target)
  if (!needle) return null
  const nodes = collectHighlightTextNodes(root)
  const { text, map } = buildNormalizedDocxText(nodes)
  const at = text.indexOf(needle)
  if (at === -1) return null

  // The needle is trimmed, so its first and last characters are never a boundary space.
  const first = map[at]!
  const last = map[at + needle.length - 1]!
  const segments: DocxHighlightSegment[] = []
  for (let n = first.node; n <= last.node; n++) {
    const node = nodes[n]!
    const start = n === first.node ? first.offset : 0
    const end = n === last.node ? last.offset + 1 : node.data.length
    // A whitespace-only stretch (markup indentation between paragraphs) has nothing to paint.
    if (node.data.slice(start, end).trim()) segments.push({ node, start, end })
  }
  return segments
}

export interface OverlayBox { left: number; top: number; width: number; height: number }

/** Where to place a highlight overlay INSIDE the render target, for one client rect of the match.
 *  The target is zoomed with `transform: scale()`, and client rects are measured after it — so an
 *  overlay placed inside the target must be divided back by the scale, or it is scaled twice. */
export function overlayBox(rect: OverlayBox, targetRect: { left: number; top: number }, scale: number): OverlayBox {
  const s = scale > 0 ? scale : 1
  return {
    left: (rect.left - targetRect.left) / s,
    top: (rect.top - targetRect.top) / s,
    width: rect.width / s,
    height: rect.height / s,
  }
}
